import { ReactNode, startTransition, useCallback } from "react";
import {
  faInfo,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Image, Text, ThemeIcon, useMantineTheme } from "@mantine/core";
import { modals } from "@mantine/modals";
import {
  helpAddTrackpoint,
  helpDeleteTrackpoint,
  helpHybridTrackpoint,
  helpInsertTrackpoint,
  helpMoveTrackpoint,
} from "assets/help";
import { getRandomLandscape, landscapeSizes } from "assets/welcomeModal";
import { ConfirmLosingTrackModalProps } from "containers/UI/Modals/ConfirmLosingTrackModal";
import { CoordsInputModalProps } from "containers/UI/Modals/CoordsInputModal";
import type { EditTrackModalProps } from "containers/UI/Modals/EditTrackModal";
import type { EditWaypointModalProps } from "containers/UI/Modals/EditWaypointModal";
import {
  LazyWelcomeModal,
  loadWelcomeModalTitle,
} from "containers/UI/Modals/LazyWelcomeModal";
import type { WelcomeModalProps } from "containers/UI/Modals/WelcomeModal";
import { useTranslation } from "react-i18next";
import { TrackWithMetadata, Waypoint } from "types/app";

/**
 * Opens the app's modals. Uses Mantine's `modals` API, which works anywhere in the app (also in providers
 * above the ModalsProvider, which the modals themselves need for their data) as long as the ModalsProvider is mounted.
 */
export const useGenericModals = () => {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  type ConfirmModalProps = {
    title: string;
    text?: string;
    children?: ReactNode;
    onCancel?: () => void;
    onConfirm: () => void;
    labels?: {
      confirm?: string;
      cancel?: string;
    };
  };
  const openConfirmModal = useCallback(
    ({
      title,
      text,
      onCancel,
      onConfirm,
      children,
      labels,
    }: ConfirmModalProps) => {
      modals.openConfirmModal({
        title: <Text size="lg">{title}</Text>,
        children: children || <Text size="sm">{text}</Text>,
        labels: {
          confirm: labels?.confirm ?? t("generic.confirm_button_label"),
          cancel: labels?.cancel ?? t("generic.cancel_button_label"),
        },
        centered: true,
        onCancel,
        onConfirm,
        confirmProps: { color: "red" },
      });
    },
    [t]
  );

  type ConfirmTrackDeleteModalProps = {
    count: number;
    onConfirm: () => void;
    onCancel?: () => void;
  };
  const openConfirmTrackDeleteModal = ({
    count,
    onConfirm,
    onCancel,
  }: ConfirmTrackDeleteModalProps) => {
    openConfirmModal({
      title: t("delete_tracks_modal.title", { count }),
      text: t("delete_tracks_modal.text", { count }),
      onConfirm,
      onCancel,
      labels: {
        confirm: t("generic.yes"),
        cancel: t("generic.no"),
      },
    });
  };

  type ConfirmWaypointDeleteModalProps = {
    count: number;
    onConfirm: () => void;
    onCancel?: () => void;
  };
  const openConfirmWaypointDeleteModal = ({
    count,
    onConfirm,
    onCancel,
  }: ConfirmWaypointDeleteModalProps) => {
    openConfirmModal({
      title: t("delete_waypoints_modal.title", { count }),
      text: t("delete_waypoints_modal.text", { count }),
      onConfirm,
      onCancel,
      labels: {
        confirm: t("generic.yes"),
        cancel: t("generic.no"),
      },
    });
  };

  type ErrorModalProps = {
    title?: string;
    text?: string | null;
  };
  const openErrorModal = useCallback(
    ({ title, text }: ErrorModalProps) => {
      modals.openContextModal({
        modal: "errorModal",
        centered: true,
        withCloseButton: false,
        title: null,
        size: "xs",
        innerProps: {
          icon: (
            <ThemeIcon
              size={60}
              variant="light"
              color="red"
              radius={100}
              style={{ fontSize: 30 }}
            >
              <FontAwesomeIcon icon={faTriangleExclamation} />
            </ThemeIcon>
          ),
          title: title ?? t("generic.error_title"),
          text: text ?? t("error_modal.generic_text"),
          buttonText: t("generic.dismiss"),
        },
      });
    },
    [t]
  );

  const openWaypointEditModal = useCallback((waypoint: Waypoint) => {
    modals.openContextModal({
      modal: "editWaypoint",
      title: <Text style={{ fontWeight: 700 }}>{waypoint.name}</Text>,
      centered: true,
      innerProps: { waypointId: waypoint.id } satisfies EditWaypointModalProps,
    });
  }, []);

  const openTrackEditModal = useCallback((track: TrackWithMetadata) => {
    modals.openContextModal({
      modal: "editTrack",
      title: <Text style={{ fontWeight: 700 }}>{track.name}</Text>,
      centered: true,
      innerProps: { trackId: track.id } satisfies EditTrackModalProps,
    });
  }, []);

  const openInfoModal = useCallback(
    (heading?: string, text?: string) => {
      modals.openContextModal({
        modal: "topIconModal",
        centered: true,
        withCloseButton: false,
        title: null,
        size: "xs",
        innerProps: {
          icon: (
            <ThemeIcon
              size={60}
              variant="light"
              color="violet"
              radius={100}
              style={{ fontSize: 30 }}
            >
              <FontAwesomeIcon icon={faInfo} />
            </ThemeIcon>
          ),
          title: heading,
          text,
          buttonText: t("generic.ok"),
          buttonColor: theme.primaryColor,
        },
      });
    },
    [t, theme.primaryColor]
  );

  const openImportGPXmodal = useCallback(() => {
    modals.openContextModal({
      modal: "importGPX",
      title: (
        <Text style={{ fontWeight: 700 }}>{t("import_GPX_modal.title")}</Text>
      ),
      centered: true,
      innerProps: {},
    });
  }, [t]);

  const openHelpModal = useCallback(() => {
    const id = modals.open({
      title: t("help_modal.title"),
      size: "lg",
      children: (
        <>
          <Text size="lg" style={{ fontWeight: "bold" }}>
            {t("help_modal.body.routing_text")}
          </Text>
          <Text>{t("help_modal.body.add_track_point_text")}</Text>
          <Image src={helpAddTrackpoint} mt="xs" mb="lg" />
          <Text>{t("help_modal.body.move_track_point_text")}</Text>
          <Image src={helpMoveTrackpoint} mt="xs" mb="lg" />
          <Text>{t("help_modal.body.insert_track_point_text")}</Text>
          <Image src={helpInsertTrackpoint} mt="xs" mb="lg" />
          <Text>{t("help_modal.body.delete_track_point_text")}</Text>
          <Image src={helpDeleteTrackpoint} mt="xs" mb="lg" />
          <Text style={{ fontWeight: "bold" }} size="lg">
            {t("help_modal.body.free_routing_text")}
          </Text>
          <Text>{t("help_modal.body.add_hybrid_track_point_text")}</Text>
          <Image src={helpHybridTrackpoint} mt="xs" mb="lg" />
          <Button fullWidth onClick={() => modals.close(id)} mt="md">
            {t("generic.ok")}
          </Button>
        </>
      ),
    });
  }, [t]);

  const openWelcomeModal = useCallback(async () => {
    // The image is the modal's largest element (LCP), so its download starts with high priority
    // in parallel with the modal's code, not only after the modal renders.
    // `sizes` is set before `srcset`, so the same candidate as in the modal is picked and then reused from cache.
    const featuredImage = getRandomLandscape();
    const preloadedImage = new window.Image();
    preloadedImage.fetchPriority = "high";
    preloadedImage.sizes = landscapeSizes;
    preloadedImage.srcset = featuredImage;
    // The modal's code is loaded first, so it opens complete without a loader
    const [WelcomeModalTitle] = await Promise.all([
      loadWelcomeModalTitle(),
      LazyWelcomeModal.preload(),
    ]);
    // Opened as a transition: React renders the modal in short slices instead of one long task
    // (the welcome modal opens with the app, while the map is loading)
    startTransition(() => {
      modals.openContextModal({
        modal: "welcome",
        title: <WelcomeModalTitle />,
        innerProps: { featuredImage } satisfies WelcomeModalProps,
        padding: 0,
        // The content is a column limited to the window height - the image and the text shrink
        // (the text scrolls) so the "don't show again" checkbox at the bottom stays visible
        styles: {
          content: {
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
          header: { padding: 20, marginBottom: 0, flexShrink: 0 },
          body: {
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          },
        },
        size: "xl",
      });
    });
  }, []);

  const openConfirmLosingTrackModal = useCallback(
    (props?: ConfirmLosingTrackModalProps) => {
      modals.openContextModal({
        modal: "confirmLosingTrack",
        title: t("confirm_losing_track_modal.title"),
        innerProps: {
          onConfirm: props?.onConfirm,
        },
        styles: {
          title: {
            fontWeight: "bold",
          },
        },
      });
    },
    [t]
  );

  /**
   * Before an own track is closed (or replaced) while its latest change isn't saved: the autosave hasn't finished
   * yet (it waits for the route and elevations) or it failed
   */
  const openConfirmLosingChangesModal = useCallback(
    ({ onConfirm }: { onConfirm: () => void }) =>
      openConfirmModal({
        title: t("editmode.close_unsaved_track_modal.title"),
        text: t("editmode.close_unsaved_track_modal.text"),
        labels: {
          confirm: t("editmode.close_unsaved_track_modal.close_button"),
        },
        onConfirm,
      }),
    [openConfirmModal, t]
  );

  const openCoordsInputModal = useCallback(
    (callback: CoordsInputModalProps["callback"]) => {
      modals.openContextModal({
        modal: "coordsInputModal",
        title: t("coords_input_modal.title"),
        size: "auto",
        innerProps: {
          callback,
        },
      });
    },
    [t]
  );

  const openSettingsModal = useCallback(() => {
    modals.openContextModal({
      modal: "settingsModal",
      title: t("account_settings_modal.title"),
      innerProps: {},
      size: "md",
    });
  }, [t]);

  return {
    openErrorModal,
    openConfirmModal,
    openWaypointEditModal,
    openTrackEditModal,
    openHelpModal,
    openImportGPXmodal,
    openInfoModal,
    openConfirmTrackDeleteModal,
    openConfirmWaypointDeleteModal,
    openConfirmLosingTrackModal,
    openConfirmLosingChangesModal,
    openWelcomeModal,
    openCoordsInputModal,
    openSettingsModal,
  };
};
